import { reportSubSessionClaimSettled } from './telemetry';
import { CLAIM_MESSAGE, DEFAULT_CLAIM_WINDOW_MS } from './wireContract';

type MessageListener = (event: MessageEvent) => void;

// A stand-in in tests: jsdom has no BroadcastChannel.
export type ClaimChannel = {
  addEventListener: (type: 'message', listener: MessageListener) => void;
  postMessage: (message: unknown) => void;
  removeEventListener: (type: 'message', listener: MessageListener) => void;
};

type ClaimChannelMessage = {
  instanceId?: string;
  subSessionId?: string;
  type?: string;
};

export type SubSessionClaim = {
  /** Resolves false when another live tab already holds the id, i.e. this tab is a copy. */
  claim: (candidateId: string) => Promise<boolean>;
  own: (subSessionId: string) => void;
  owned: () => string | null;
};

const newInstanceId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
};

/**
 * Asks the other tabs of this origin whether a candidate id is really this tab's, since a
 * duplicated tab copies sessionStorage. Only live documents answer, so a reload never gets a
 * false objection, and a collision with a closed or frozen tab goes unnoticed.
 */
export const createSubSessionClaim = ({
  channel,
  instanceId = newInstanceId(),
  windowMs = DEFAULT_CLAIM_WINDOW_MS,
}: {
  channel: ClaimChannel;
  instanceId?: string;
  windowMs?: number;
}): SubSessionClaim => {
  let defendedId: string | null = null;

  const readFromAnotherTab = (event: MessageEvent): ClaimChannelMessage | null => {
    const data = event.data as ClaimChannelMessage | null;
    return data && data.instanceId !== instanceId ? data : null;
  };

  channel.addEventListener('message', (event) => {
    const data = readFromAnotherTab(event);
    if (data?.type === CLAIM_MESSAGE.CLAIM && defendedId && data.subSessionId === defendedId) {
      channel.postMessage({ type: CLAIM_MESSAGE.CONFLICT, subSessionId: defendedId, instanceId });
    }
  });

  return {
    claim: (candidateId) =>
      new Promise<boolean>((resolve) => {
        // Defended at once, so two tabs claiming the same id object to each other.
        defendedId = candidateId;
        const claimedAt = performance.now();

        const settle = (mayKeep: boolean): void => {
          clearTimeout(timeoutId);
          channel.removeEventListener('message', onConflict);
          // Only give up the candidate itself; an id assigned meanwhile stays defended.
          if (!mayKeep && defendedId === candidateId) {
            defendedId = null;
          }
          reportSubSessionClaimSettled({
            durationMs: performance.now() - claimedAt,
            isKept: mayKeep,
            windowMs,
          });
          resolve(mayKeep);
        };

        const onConflict = (event: MessageEvent): void => {
          const data = readFromAnotherTab(event);
          if (data?.type === CLAIM_MESSAGE.CONFLICT && data.subSessionId === candidateId) {
            settle(false);
          }
        };

        const timeoutId = setTimeout(() => settle(true), windowMs);
        channel.addEventListener('message', onConflict);
        channel.postMessage({ type: CLAIM_MESSAGE.CLAIM, subSessionId: candidateId, instanceId });
      }),
    own: (subSessionId) => {
      defendedId = subSessionId;
    },
    owned: () => defendedId,
  };
};
